import {
  Box,
  HStack,
  Icon,
  Show,
  Text,
  ThemeColorPath,
  ThemeColorTextPath,
} from 'platform/foundation';
import styled from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {includes} from 'ramda';

import {VehicleFinancingBalloonPayment} from '@dms/api/sales';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {RATE_MATRIX_BALLOON_BRACKETS} from '../constants/RATE_MATRIX_BALLOON_BRACKETS';
import {createActivationKeyDownHandler} from '../utils/createActivationKeyDownHandler';

interface BracketSurfaceProps {
  $isDisabled: boolean;
}

/**
 * The chip's own interactive surface — the shared `Clickable` has no disabled a11y contract
 * (`aria-disabled`, forced `tabindex="-1"`) and no design-system focus ring, so the chip carries
 * both itself, mirroring the rate matrix cell.
 */
const BracketSurface = styled.div<BracketSurfaceProps>`
  cursor: ${({$isDisabled}) => ($isDisabled ? 'initial' : 'pointer')};
  pointer-events: ${({$isDisabled}) => ($isDisabled ? 'none' : 'all')};

  /* The design-system focus ring: theme.shadows.active is the semantic focus/active token every
     platform input shares. The radius matches the pill-shaped chip inside. */
  &:focus-visible {
    outline: none;
    border-radius: ${({theme}) => theme.radii.large};
    box-shadow: ${({theme}) => theme.shadows.active};
  }
`;

interface BracketStyle {
  backgroundColor: ThemeColorPath;
  borderColor: ThemeColorPath;
  textColor: ThemeColorTextPath;
  showStar: boolean;
  starColor: ThemeColorPath;
}

interface RateMatrixBalloonBracketsProps extends RequiredTestIdProps {
  availableBalloonBrackets: VehicleFinancingBalloonPayment[];
  selectedBracket: VehicleFinancingBalloonPayment | Nullish;
  defaultBalloonPayment: VehicleFinancingBalloonPayment | Nullish;
  isReadOnly: boolean;
  onSelect: (bracket: VehicleFinancingBalloonPayment) => void;
}

export function RateMatrixBalloonBrackets(props: RateMatrixBalloonBracketsProps) {
  return (
    <HStack spacing={2} wrap>
      {RATE_MATRIX_BALLOON_BRACKETS.map((bracket) => {
        const isDefault = bracket === props.defaultBalloonPayment;
        const isSelected = bracket === props.selectedBracket;
        const isConfigured = includes(bracket, props.availableBalloonBrackets);

        const style = match<[boolean, boolean, boolean], BracketStyle>([
          isDefault,
          isSelected,
          isConfigured,
        ])
          .with([Pattern.any, true, Pattern.any], ([isDefaultBracket]) => ({
            backgroundColor: 'general.accent',
            borderColor: 'general.accent',
            textColor: 'white',
            showStar: isDefaultBracket,
            starColor: 'text.white',
          }))
          .with([true, false, Pattern.any], () => ({
            backgroundColor: 'general.white',
            borderColor: 'general.accent',
            textColor: 'primary',
            showStar: true,
            starColor: 'general.accent',
          }))
          .with([false, false, true], () => ({
            backgroundColor: 'general.white',
            borderColor: 'general.accent',
            textColor: 'primary',
            showStar: false,
            starColor: 'general.accent',
          }))
          .with([false, false, false], () => ({
            backgroundColor: 'general.white',
            borderColor: 'palettes.neutral.40.100',
            textColor: 'tertiary',
            showStar: false,
            starColor: 'general.accent',
          }))
          .exhaustive();

        return (
          <BracketSurface
            key={bracket}
            role="button"
            tabIndex={props.isReadOnly ? -1 : 0}
            aria-disabled={props.isReadOnly || undefined}
            aria-pressed={isSelected}
            $isDisabled={props.isReadOnly}
            onClick={props.isReadOnly ? undefined : () => props.onSelect(bracket)}
            onKeyDown={
              props.isReadOnly
                ? undefined
                : createActivationKeyDownHandler(() => props.onSelect(bracket))
            }
            data-testid={suffixTestId(`bracket-[${bracket}]`, props)}
          >
            <Box
              paddingHorizontal={3}
              paddingVertical={2}
              borderRadius="large"
              border="1px solid"
              borderColor={style.borderColor}
              backgroundColor={style.backgroundColor}
            >
              <HStack spacing={1} align="center">
                {/* Passive indicator only — the default is set via the dedicated field below. */}
                <Show when={style.showStar}>
                  <Icon value="toggle/star" size={4} color={style.starColor} />
                </Show>
                <Text size="small" color={style.textColor} noWrap>
                  {`${bracket} %`}
                </Text>
              </HStack>
            </Box>
          </BracketSurface>
        );
      })}
    </HStack>
  );
}
